import { axiosInstance } from '../clients/axiosClient';
import {
  getFilenameFromResponseHeaders,
  getOnboardingFormPdfFilename,
  getPreScanQuestionnairePdfFilename,
  parseAxiosBlobError,
} from '../utils/fileDownload';
import {
  MEMBERS_API_PREFIX,
  API_V1_PREFIX,
  DOCTORS_API_PREFIX,
  NOTES_API_PREFIX,
  NUTRITIONIST_API_PREFIX
} from '../utils/constants';
import {
  CreateUserResponse,
  HTTPValidationError,
  UpdateMemberRequestPydantic,
  ListMembersResponse,
  ListMembersResponseElement,
  ListInChargeAdminsResponse,
  GetMemberResponse,
  GetMemberProfileResponse,
  MedicalCardRequestPydantic,
  CompleteMemberResponse,
  AssignNutritionistRequestPydantic,
  SetMemberAddressRequestPydantic,
  SetMemberAddressResponse,
  CreateMemberRequestPydantic,
  CreateNoteRequest,
  CreateNoteResponse,
  ListNotesResponse,
  ListNoteResponseElement,
  EditNoteRequest,
  EditNoteResponse,
  DeleteNoteRequest,
  AssignNutritionistResponse,
  AssignDoctorRequestPydantic,
  AssignDoctorResponse,
  AssignInChargeAdminRequestPydantic,
  AssignInChargeAdminResponse,
  AssignRecommendationRequestPydantic,
  AssignRecommendationResponse,
  UnassignRecommendationRequestPydantic,
  UnassignRecommendationResponse,
  GetMedicalCardResponse,
  DeactivateMemberRequest,
  DeactivateMemberResponse,
  BiologicalAgeAuditRecord,
  GetBiologicalAgeWrapper,
  PublishBioAgeResponse,
  UpdateMedicalCardRequestPydantic,
  UpdateMedicalCardResponse
} from '../types/apiGeneratedTypes';
import { LoadingStrategy } from '../types/membersTypes';

export const getMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  status?: string,
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (status) {
      params.append('membership_status', status);
    }

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) { params.append('sort_order', direction); }
    if (columnKey) { params.append('sort_by', columnKey); }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}list?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch members');
  }
};

export interface ClinicalDashboardBiomarker {
  name: string;
  value: string;
}

export interface ClinicalDashboardRow {
  member_id: string;
  member_name: string;
  gender: string | null;
  dob: string | null;
  age: number | null;
  health_concerns: string[];
  appointment_date: string | null;
  biomarkers: ClinicalDashboardBiomarker[];
  biological_age_status: 'published' | 'not_published';
  biological_age_value: number | null;
}

export interface ClinicalDashboardResponse {
  rows: ClinicalDashboardRow[];
  total_appointment_rows: number;
  total_members: number;
  members_with_results: number;
  members_without_results: number;
  average_biomarker_value: number | null;
  numeric_biomarker_values_count: number;
  published_biological_age_count: number;
  unpublished_biological_age_count: number;
}

export const getClinicalDashboard = async (
  limit: number = 10,
  offset: number = 0,
  member_name?: string,
  biomarker_name?: string,
  biomarker_limit: number = 100
): Promise<ClinicalDashboardResponse> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      biomarker_limit: biomarker_limit.toString(),
    });

    if (member_name?.trim()) {
      params.append('member_name', member_name.trim());
    }
    if (biomarker_name?.trim()) {
      params.append('biomarker_name', biomarker_name.trim());
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}clinical-dashboard?${params.toString()}`
    );
    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch clinical dashboard');
  }
};

export interface OldBloodDashboardRow {
  member_id: string;
  member_name: string;
  gender: string | null;
  dob: string | null;
  age: number | null;
  appointment_date: string | null;
  biomarkers: ClinicalDashboardBiomarker[];
}

export interface OldBloodDashboardResponse {
  rows: OldBloodDashboardRow[];
  total_appointment_rows: number;
  total_members: number;
  members_with_results: number;
  members_without_results: number;
  average_biomarker_value: number | null;
  numeric_biomarker_values_count: number;
}

export const getOldBloodDashboard = async (
  limit: number = 10,
  offset: number = 0,
  member_name?: string,
  biomarker_name?: string,
  biomarker_limit: number = 100
): Promise<OldBloodDashboardResponse> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      biomarker_limit: biomarker_limit.toString(),
    });

    if (member_name?.trim()) {
      params.append('member_name', member_name.trim());
    }
    if (biomarker_name?.trim()) {
      params.append('biomarker_name', biomarker_name.trim());
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}old-blood-results-dashboard?${params.toString()}`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail)
      );
    }
    throw new Error(error.message || 'Failed to fetch old blood dashboard');
  }
};

export interface OperationsTatDashboardRow {
  member_id: string;
  member_name: string;
  subscription_created_at: string | null;
  booking_appointment_date: string | null;
  hl7_received_at: string | null;
  gp_review_date: string | null;
  nutritionist_appointment_date: string | null;
  tat_booking_minutes: number | null;
  tat_receiving_results_minutes: number | null;
  tat_laboratory_minutes: number | null;
  tat_results_published_minutes: number | null;
  tat_nutritionist_minutes: number | null;
  tat_overall_minutes: number | null;
}

export interface OperationsTatDashboardResponse {
  rows: OperationsTatDashboardRow[];
  total_count: number;
  total_members: number;
  members_with_hl7: number;
  members_with_booking: number;
  members_with_nutritionist: number;
}

export const getOperationsTatDashboard = async (
  limit: number = 10,
  offset: number = 0,
  member_name?: string
): Promise<OperationsTatDashboardResponse> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (member_name?.trim()) {
      params.append('member_name', member_name.trim());
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}operations-tat-dashboard?${params.toString()}`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail)
      );
    }
    throw new Error(error.message || 'Failed to fetch operations TAT dashboard');
  }
};

export const getMembersListByUniqueId = async (
  unique_id: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist'
): Promise<ListMembersResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}list?loading_strategy=${loading_strategy}&unique_id=${unique_id}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getInChargeAdminList =
  async (): Promise<ListInChargeAdminsResponse> => {
    try {
      const { data } = await axiosInstance.get(
        `${API_V1_PREFIX}${MEMBERS_API_PREFIX}in-charge-admin-list`
      );
      return data;
    } catch (error: any) {
      throw new Error(
        error.message || 'Failed to fetch in charge admin list'
      );
    }
  };

export const getDoctorsMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy = 'with_doctor',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  member_status?: 'all_members' | 'my_members',
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
      subscribe: 'true'
    });

    if (member_status) {
      params.append('member_status', member_status);
    }

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) {
      params.append('sort_order', direction);
    }
    if (columnKey) {
      params.append('sort_by', columnKey);
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${DOCTORS_API_PREFIX}members?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getNutritionistsMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy = 'with_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
      subscribe: 'true'
    });

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) {
      params.append('sort_order', direction);
    }
    if (columnKey) {
      params.append('sort_by', columnKey);
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${NUTRITIONIST_API_PREFIX}members?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getUnpaidMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
      subscribe: 'false'
    });

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) {
      params.append('sort_order', direction);
    }
    if (columnKey) {
      params.append('sort_by', columnKey);
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}list?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

// New API function using the dedicated endpoint for member status filtering
export const getMembersListWithStatus = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  member_status?:
    | 'active'
    | 'disabled'
    | 'free'
    | 'all'
    | 'blood_incomplete_booking'
    | 'scan_incomplete_booking',
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (member_status) {
      params.append('member_status', member_status);
    }

    if (membership_status && membership_status !== 'all') {
      params.append('membership_status', membership_status);
    }

    if (membership_type && membership_type !== 'all') {
      params.append('membership_type', membership_type);
    }

    if (in_charge_admin_name) {
      const normalizedInCharge = String(in_charge_admin_name).replace(/\s+/g, ' ').trim();
      if (normalizedInCharge) {
        params.append('in_charge_admin_name', normalizedInCharge);
      }
    }
    if (assigned_doctor_id) {
      params.append('assigned_doctor_id', assigned_doctor_id);
    }
    if (subscription_start_date) {
      params.append('subscription_start_date', subscription_start_date);
    }
    if (subscription_end_date) {
      params.append('subscription_end_date', subscription_end_date);
    }
    if (tag_id !== undefined) {
      params.append('tag_id', String(tag_id));
    }

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) { params.append('sort_order', direction); }
    if (columnKey) { params.append('sort_by', columnKey); }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}members/data/list?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch members with status');
  }
};

// Convenience functions for different member statuses
export const getActiveMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'active', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};

export const getDisabledMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'disabled', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};



export const getAllMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'all', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};

export const getFreeMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'free', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};

export const getBloodIncompleteBookingMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'blood_incomplete_booking', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};

export const getScanIncompleteBookingMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
  direction?: 'asc' | 'desc',
  columnKey?: string,
  membership_status?: string,
  in_charge_admin_name?: string,
  membership_type?: string,
  assigned_doctor_id?: string,
  subscription_start_date?: string,
  subscription_end_date?: string,
  tag_id?: number,
): Promise<ListMembersResponse> => {
  return getMembersListWithStatus(offset, limit, search, loading_strategy, direction, columnKey, 'scan_incomplete_booking', membership_status, in_charge_admin_name, membership_type, assigned_doctor_id, subscription_start_date, subscription_end_date, tag_id);
};

/** Search across all member list tabs — `member_status=all` excludes some buckets (e.g. blood_incomplete_booking). */
const MERGE_SEARCH_MEMBER_STATUSES = [
  'active',
  'disabled',
  'free',
  'blood_incomplete_booking',
  'scan_incomplete_booking',
] as const;

export const searchMembersForMerge = async (
  search: string,
  limit: number = 15,
  loading_strategy: LoadingStrategy = 'with_doctor_and_nutritionist',
): Promise<ListMembersResponse> => {
  const trimmed = search.trim();
  if (!trimmed) {
    return { members: [], total_count: 0, scan_count: 0, blood_count: 0 };
  }

  const perStatusLimit = Math.max(limit, 10);
  const responses = await Promise.all(
    MERGE_SEARCH_MEMBER_STATUSES.map((member_status) =>
      getMembersListWithStatus(
        0,
        perStatusLimit,
        trimmed,
        loading_strategy,
        'asc',
        undefined,
        member_status,
      ).catch(() => ({
        members: [] as ListMembersResponseElement[],
        total_count: 0,
        scan_count: 0,
        blood_count: 0,
      }))
    )
  );

  const seen = new Set<string>();
  const members: ListMembersResponseElement[] = [];

  for (const response of responses) {
    for (const member of response.members ?? []) {
      if (seen.has(member.id)) continue;
      seen.add(member.id);
      members.push(member);
      if (members.length >= limit) break;
    }
    if (members.length >= limit) break;
  }

  return { members, total_count: members.length, scan_count: 0, blood_count: 0 };
};

export const getUnpaidDoctorsMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy = 'with_doctor',
  direction?: 'asc' | 'desc',
  columnKey?: string,
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
      subscribe: 'false'
    });

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }
    if (direction) {
      params.append('sort_order', direction);
    }
    if (columnKey) {
      params.append('sort_by', columnKey);
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${DOCTORS_API_PREFIX}members?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getUnpaidNutritionistsMembersList = async (
  offset: number = 0,
  limit: number = 10,
  search?: string,
  loading_strategy = 'with_nutritionist'
): Promise<ListMembersResponse> => {
  try {
    const params = new URLSearchParams({
      loading_strategy,
      limit: limit.toString(),
      offset: offset.toString(),
      subscribe: 'false'
    });

    if (search) {
      if (search.includes('@')) {
        params.append('email', search);
      } else if (/^\d+$/.test(search.replace(/\s/g, ''))) {
        params.append('phone', search.replace(/\s/g, ''));
      } else {
        params.append('full_name', search);
      }
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${NUTRITIONIST_API_PREFIX}members?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const createMember = async (
  newMemberRequestObject: CreateMemberRequestPydantic
): Promise<CreateUserResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}`,
      newMemberRequestObject
    );

    return data;
  } catch (error: any) {
    if (error?.response?.data?.detail) {
      return error?.response?.data;
    }
    throw new Error(error);
  }
};

export const updateMember = async (
  memberRequestObject: UpdateMemberRequestPydantic
): Promise<UpdateMemberRequestPydantic | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}`,
      memberRequestObject
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const assignAddress = async (
  memberAddress: SetMemberAddressRequestPydantic
): Promise<SetMemberAddressResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}set-address`,
      memberAddress
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMember = async (id: string): Promise<GetMemberResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${id}?load_strategy=with_doctor_and_nutritionist`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getCurrentMemberProfile = async (
  load_strategy: LoadingStrategy = 'with_doctor_and_nutritionist'
): Promise<GetMemberProfileResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}?load_strategy=${load_strategy}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMemberMedicalCard = async (
  id: string
): Promise<GetMedicalCardResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}medical-card/${id}/`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const updateMemberMedicalCard = async (
  updateRequest: UpdateMedicalCardRequestPydantic
): Promise<UpdateMedicalCardResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}update/med-card`,
      updateRequest
    );

    return data;
  } catch (error: any) {
    if (error?.response?.data?.detail || error?.response?.data?.description) {
      return error?.response?.data;
    }
    throw error;
  }
};

export const completeMemberForm = async (
  memberProfileRequestObject: MedicalCardRequestPydantic
): Promise<CompleteMemberResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}complete-profile`,
      memberProfileRequestObject
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const assignDoctorToMember = async (
  ids: AssignDoctorRequestPydantic
): Promise<AssignDoctorResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}assign-doctor`,
      ids
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const assignNutritionistToMember = async (
  ids: AssignNutritionistRequestPydantic
): Promise<AssignNutritionistResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}assign-nutritionist`,
      ids
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const assignInChargeAdminToMember = async (
  payload: AssignInChargeAdminRequestPydantic
): Promise<AssignInChargeAdminResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}assign-in-charge-admin`,
      payload
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const resendFormToMember = async (user_id: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}resend-form`,
      { user_id }
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const resendPaymentLink = async (user_id: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}resend-payment-link`,
      { user_id }
    );
    return data;
  } catch (error: any) {
    if (error?.response?.data?.detail) {
      return error?.response?.data;
    }
    throw new Error(error);
  }
};

export interface MemberPaymentItem {
  id?: string;
  amount?: number | string | null;
  currency?: string | null;
  date?: string | null;
  payment_method?: string | null;
  payment_method_type?: string | null;
  payment_status?: string | null;
  status?: string | null;
  invoice_id?: string | null;
  charge_id?: string | null;
  payment_intent_id?: string | null;
  failure_code?: string | null;
  failure_message?: string | null;
}

export interface GetMemberPaymentsResponse {
  member_id: string;
  payments: MemberPaymentItem[];
}

export const getMemberPayments = async (
  member_id: string
): Promise<GetMemberPaymentsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/payment-history`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export interface SyncMemberPaymentsResponse {
  member_id: string;
  email: string;
  synced_count: number;
  skipped_count: number;
  payments: unknown[];
}

export const syncMemberPayments = async (
  member_id: string
): Promise<SyncMemberPaymentsResponse> => {
  const { data } = await axiosInstance.post(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/payments/sync`
  );
  return data;
};

export interface SubscriptionPaymentFailureResponse {
  has_failed_payment: boolean;
  subscription_status?: string | null;
  invoice_id?: string | null;
  invoice_status?: string | null;
  amount_due_minor?: number | null;
  amount_due?: number | null;
  currency?: string | null;
  attempt_count: number;
  next_payment_attempt?: string | null;
  failure_reason?: string | null;
  failure_code?: string | null;
  decline_code?: string | null;
  failure_message?: string | null;
  payment_intent_id?: string | null;
  can_copy_update_payment_link: boolean;
}

export const getSubscriptionPaymentFailure = async (
  member_id: string
): Promise<SubscriptionPaymentFailureResponse> => {
  const { data } = await axiosInstance.get(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/subscription/payment-failure`
  );
  return data;
};

export interface UpdatePaymentLinkResponse {
  url: string;
  invoice_id: string;
  link_type: string;
}

export const getUpdatePaymentLink = async (
  member_id: string
): Promise<UpdatePaymentLinkResponse> => {
  const { data } = await axiosInstance.get(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/subscription/update-payment-link`
  );
  return data;
};

export const createMemberNote = async (
  requestObj: CreateNoteRequest
): Promise<CreateNoteResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${NOTES_API_PREFIX}`,
      requestObj
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const updateMemberNote = async (
  requestObj: EditNoteRequest
): Promise<EditNoteResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${NOTES_API_PREFIX}`,
      requestObj
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMemberNotesList = async (
  member_id: string,
  limit: number,
  offset: number
): Promise<ListNotesResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${NOTES_API_PREFIX}list?member_id=${member_id}&limit=${limit}&offset=${offset}`
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};
export const getMemberBioAge = async (
  member_id: string
): Promise<GetBiologicalAgeWrapper> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}bio-age?member_id=${member_id}`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMemberBioAgeDetails = async (
  memberId: string
): Promise<BiologicalAgeAuditRecord> => {
  try {
    const { data } = await axiosInstance.get(
      `/api/v1/members/${memberId}/bio-age/details`
    );
    return data?.bio_ages;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Failed to fetch bio age details');
  }
};

export const publishBiologicalAge = async (
  biologicalAgeId: number | string,
  memberId: string
): Promise<PublishBioAgeResponse> => {
  try {
    const { data } = await axiosInstance.post<PublishBioAgeResponse>(
      '/api/v1/members/biological-ages/publish', null, {
      params: {
        biological_age_id: biologicalAgeId,
        member_id: memberId,
      },
    }
    );
    return data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || 'Failed to publish biological age'
    );
  }
};

/** POST /api/v1/members/bio-age/recalculate?member_id= */
export interface RecalculateBioAgeResponse {
  success: boolean;
  message: string;
  biological_age_id: number | null;
  biological_age_value: number | null;
  label: string | null;
  missing_biomarkers?: string[] | null;
}

/** Appends missing biomarker names to the API message for toasts / UI. */
export function formatRecalculateBioAgeErrorMessage(
  message: string | undefined | null,
  missing?: string[] | null
): string {
  const base = (message && message.trim()) || 'Could not recalculate biological age.';
  const names = (missing ?? []).filter((n) => Boolean(n && String(n).trim()));
  if (!names.length) return base;
  return `${base}\n\nMissing biomarkers (${names.length}): ${names.join(', ')}`;
}

export const recalculateBiologicalAge = async (
  memberId: string
): Promise<RecalculateBioAgeResponse> => {
  try {
    const { data } = await axiosInstance.post<RecalculateBioAgeResponse>(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}bio-age/recalculate`,
      null,
      { params: { member_id: memberId } }
    );
    return data;
  } catch (error: any) {
    const d = error?.response?.data;
    const detail = d?.detail;
    const detailStr =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail
            .map((x: { msg?: string }) => x?.msg || '')
            .filter(Boolean)
            .join(', ')
          : '';
    let msg =
      d?.description ||
      detailStr ||
      d?.message ||
      'Failed to recalculate biological age';
    msg = formatRecalculateBioAgeErrorMessage(msg, d?.missing_biomarkers);
    throw new Error(msg);
  }
};

export const deleteNote = async (
  deleteObj: DeleteNoteRequest
): Promise<any> => {
  try {
    await axiosInstance.delete(`${API_V1_PREFIX}${NOTES_API_PREFIX}`, {
      data: deleteObj
    });
    return true;
  } catch (error: any) {
    throw error;
  }
};

export const assignRecommendationToMember = async (
  assignRecommendationRequestObject: AssignRecommendationRequestPydantic
): Promise<AssignRecommendationResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}assign-recommendation`,
      assignRecommendationRequestObject
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const unassignRecommendationFromMember = async (
  unassignRecommendationRequestObject: UnassignRecommendationRequestPydantic
): Promise<UnassignRecommendationResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}unassign-recommendation`,
      unassignRecommendationRequestObject
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const deactivateMember = async (
  request: DeactivateMemberRequest
): Promise<DeactivateMemberResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}deactivate`,
      request
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export type MergeMemberProfilesRequest = {
  primary_member_id: string;
  source_member_id: string;
};

export type MergeMemberProfilesResponse = {
  status: string;
  primary_member_id: string;
  source_member_id: string;
  reassigned_tables?: Record<string, number>;
  message?: string;
};

export const mergeMemberProfiles = async (
  request: MergeMemberProfilesRequest
): Promise<MergeMemberProfilesResponse> => {
  const { data } = await axiosInstance.post(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}merge-profiles`,
    request
  );
  return data;
};

export const activateMember = async (
  request: DeactivateMemberRequest
): Promise<DeactivateMemberResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}activate`,
      request
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const assignInuviFileToMember = async (
  member_id: string,
  file_key: string
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}assign-inuvi-file`,
      { member_id, file_key }
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};

export const sendDoctorLink = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-doctor-link`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const sendNutritionistLink = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-nutritionist-link`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const sendBloodConsultLink = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-blood-consult-link`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const sendFbsConsultLink = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-fbs-consult-link`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const sendMRIScanReviewInvitation = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-mri-debrief-link`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const sendPreScanConsultationInvitation = async (
  memberId: string
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/send-pre-scan-consultation-invitation`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const repeatBloodTestBooking = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/repeat-blood-test-booking`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const unassignFileFromMember = async (
  fileId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}files/${fileId}`
    );
  } catch (error: any) {
    throw error;
  }
};

export const listMemberInuviFiles = async (
  memberId: string
): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/inuvi-files`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const bulkAddMembers = async (members: any[]): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/bulk`,
      { members: members }
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Unknown error');
  }
};

export const getOnboardingLink = async (memberId: string): Promise<{ link: string }> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}onboarding-link`,
      { params: { user_id: memberId } }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getPreScanQuestionnaireLink = async (memberId: string): Promise<{ link: string }> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}pre-scan-questionnaire-link`,
      { params: { user_id: memberId } }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const sendPreScanQuestionnaire = async (
  memberId: string
): Promise<{ status: string; link: string }> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}send-pre-scan-questionnaire`,
      { member_id: memberId }
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const checkEmail = async (email: string): Promise<{ status: string; description?: string }> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}check-email`,
      { email }
    );
    return data;
  } catch (error: any) {
    // Handle 404 specifically - API endpoint doesn't exist yet
    if (error.response?.status === 404) {
      return { status: 'error', description: 'Email check endpoint not available' };
    }
    // Handle other errors
    if (error.response?.data?.detail) {
      return { status: 'error', description: error.response.data.detail };
    }
    throw new Error(error);
  }
};

export interface AuditLog {
  id: number;
  member_id?: string;
  action_type: string;
  performed_by_id: string;
  performed_by_name: string;
  performed_by_role: string;
  action_details: any;
  performed_at: string;
}

export interface AuditLogsResponse {
  audit_logs: AuditLog[];
  total_count: number;
  status: string;
}

export interface ChangeVipStatusResponse {
  status: string;
  is_vip: boolean;
}

export interface MemberGpSummaryResponse {
  reply?: string;
  success?: boolean;
  performed_at?: string;
  has_biomarker_results?: boolean;
  has_scan_information?: boolean;
  status?: string;
}

export const getMemberGpSummary = async (
  memberId: string
): Promise<MemberGpSummaryResponse | null> => {
  try {
    const { data } = await axiosInstance.get<MemberGpSummaryResponse>(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs/gp-summary`
    );
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export interface MemberPersonalisedMonitoringPlanResponse {
  id?: number;
  reply?: string;
  success?: boolean;
  performed_at?: string;
  name?: string | null;
  reference_number?: string | null;
  has_biomarker_results?: boolean;
  has_scan_information?: boolean;
  status?: string;
}

export interface MemberPersonalisedMonitoringPlanHistoryItem {
  id: number;
  performed_at: string;
  reply: string;
  name?: string | null;
  reference_number?: string | null;
}

export interface MemberPersonalisedMonitoringPlanHistoryResponse {
  items: MemberPersonalisedMonitoringPlanHistoryItem[];
  status?: string;
}

export const getMemberPersonalisedMonitoringPlan = async (
  memberId: string
): Promise<MemberPersonalisedMonitoringPlanResponse | null> => {
  try {
    const { data } = await axiosInstance.get<MemberPersonalisedMonitoringPlanResponse>(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs/personalised-monitoring-plan`
    );
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getMemberPersonalisedMonitoringPlanHistory = async (
  memberId: string
): Promise<MemberPersonalisedMonitoringPlanHistoryItem[]> => {
  try {
    const { data } = await axiosInstance.get<MemberPersonalisedMonitoringPlanHistoryResponse>(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs/personalised-monitoring-plan/history`
    );
    return Array.isArray(data?.items) ? data.items : [];
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const updateMemberPersonalisedMonitoringPlanName = async (
  memberId: string,
  planId: number,
  name: string
): Promise<MemberPersonalisedMonitoringPlanHistoryItem> => {
  const { data } = await axiosInstance.patch<MemberPersonalisedMonitoringPlanHistoryItem>(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs/personalised-monitoring-plan/history/${planId}`,
    { name: name.trim() }
  );
  return data;
};

export const saveMemberPersonalisedMonitoringPlan = async (
  memberId: string,
  reply: string,
  name?: string
): Promise<MemberPersonalisedMonitoringPlanResponse> => {
  const { data } = await axiosInstance.post<MemberPersonalisedMonitoringPlanResponse>(
    `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs/save-personalised-monitoring-plan`,
    {
      reply: reply.trim(),
      ...(name?.trim() ? { name: name.trim() } : {}),
    }
  );
  return data;
};

export const getMemberAuditLogs = async (
  memberId: string,
  limit: number = 100,
  offset: number = 0
): Promise<AuditLogsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/audit-logs`,
      { params: { limit, offset } }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getAllMemberAuditLogs = async (
  limit: number = 100,
  offset: number = 0
): Promise<AuditLogsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}audit-logs/all`,
      { params: { limit, offset } }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getNoteById = async (note_id: number): Promise<ListNoteResponseElement> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${NOTES_API_PREFIX}${note_id}`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getOnboardingData = async (): Promise<Blob> => {
  try {
    const { data } = await axiosInstance.get(`${API_V1_PREFIX}${MEMBERS_API_PREFIX}onboarding-data/export`, {
      responseType: 'blob'
    });
    return data;
  } catch (error: any) {
    throw error
  }
}
export const generateInuviPdf = async (member_id: string): Promise<Blob> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/inuvi-files/pdf`,
      { responseType: 'blob' }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};
export const generateSingleInuviPdf = async (member_id: string, file_id: number): Promise<Blob> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/inuvi-files/${file_id}/pdf`,
      { responseType: 'blob' }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const downloadPreScanQuestionnairePdf = async (
  member_id: string,
  options?: { memberName?: string; submittedAt?: unknown }
): Promise<void> => {
  try {
    const { data, headers } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/pre-scan-questionnaire-pdf`,
      { responseType: 'blob' }
    );
    const fileName = options?.submittedAt
      ? getPreScanQuestionnairePdfFilename(
          options.memberName ?? '',
          options.submittedAt
        )
      : getFilenameFromResponseHeaders(
          headers as Record<string, string | string[] | null | undefined>
        );
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw await parseAxiosBlobError(error);
  }
};

export const downloadOnboardingFormPdf = async (
  member_id: string,
  options?: { memberName?: string; submittedAt?: unknown }
): Promise<void> => {
  try {
    const { data, headers } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/onboarding-form-pdf`,
      { responseType: 'blob' }
    );
    const fileName = options?.submittedAt
      ? getOnboardingFormPdfFilename(options.memberName ?? '', options.submittedAt)
      : getFilenameFromResponseHeaders(
          headers as Record<string, string | string[] | null | undefined>
        );
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw await parseAxiosBlobError(error);
  }
};

export const uploadMemberFile = async (
  member_id: string,
  file: File,
  file_type: string,
  file_name?: string,
  is_private?: boolean
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', file_type);
    if (file_name) {
      formData.append('file_name', file_name);
    }
    if (is_private !== undefined) {
      formData.append('is_private', is_private.toString());
    }

    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMemberFiles = async (
  member_id: string,
  offset: number = 0,
  limit: number = 10
): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${member_id}/files`,
      {
        params: {
          offset,
          limit
        }
      }
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const deleteMemberFile = async (
  memberId: string,
  fileId: number | string
): Promise<any> => {
  try {
    const { data } = await axiosInstance.delete(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/files/${fileId}`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const getMemberFile = async (
  fileId: number | string
): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}get-file-by-id/${fileId}`
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};


export const assignMemberTags = async (payload: {
  member_id: string;
  tag_ids: number[];
}) => {
  try {
    const { data } = await axiosInstance.put(
      `${API_V1_PREFIX}${MEMBERS_API_PREFIX}set-tags`,
      payload
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};